/**
 * منوی سه‌خطِ گوشی — چیزی که زیرِ ۸۶۰ پیکسل جای نوارِ کناری و دکمه‌های هدر
 * رو می‌گیره.
 *
 * چرا نوارِ کناری این‌جا تکرار نمی‌شه: اون نوار با **هاور** باز می‌شه و صفحه‌ی
 * لمسی هاور نداره. به‌جای شبیه‌سازیِ همون رفتار با ضربه، این منو مستقیم به
 * مسیرهای واقعی می‌ره (`/signals` و `/friends`) — همون صفحه‌هایی که پنلِ
 * دسکتاپ هم نشونشون می‌ده، فقط تمام‌عرض. یعنی باز هم دو تا پیاده‌سازی از فید
 * و دوستان نداریم.
 *
 * کشوی باز از زیرِ هدر شروع می‌شه، نه از بالای صفحه: دکمه‌ی سه‌خط سرِ جاش
 * می‌مونه و همون دکمه می‌بنددش — پس بستن دقیقاً همون‌جاییه که باز کردن بود.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { t, useLocale } from '@/core/lib/i18n';
import { useSession } from '@/core/session';
import { useTheme } from '@/ui/theme';
import { Avatar } from '@/ui/kit';
import { CloseIcon, MenuIcon } from '@/ui/icons';
import { useNewSignal } from '@/features/signals/NewSignalGuard';
import type { NotifIndicators } from '@/features/notifications/useNotifIndicators';
import { returnHere } from '@/features/auth/returnHere';

interface Props {
  dots: NotifIndicators['dots'];
  guest: boolean;
  /**
   * تورِ راهنما باز است (WHOSUP-83). روی صفحه‌ی باریک همه‌ی مقصدهایی که تور
   * از آن‌ها حرف می‌زند پشتِ همین دکمه‌اند، پس اشاره‌ی هر قدمی به خودِ دکمه
   * است — نه هیچ‌جا، که روی گوشی حالتِ قبل بود.
   */
  pointing?: boolean;
}

export function MobileMenu({ dots, guest, pointing = false }: Props) {
  useLocale();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { pathname } = location;
  const { isDark, toggle } = useTheme();
  const { profile, signOut } = useSession();
  const nav = useNavigate();
  // «هنوز دوستی نداری» — یه‌بار، قبل از باز شدنِ فرم. نگاه کن به NewSignalGuard.
  const newSignal = useNewSignal();

  // رفتن به یه صفحه یعنی کارِ منو تموم شد. بدونِ این، کشو روی صفحه‌ی مقصد
  // باز می‌موند و کاربر باید دوباره می‌بستش.
  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    // قفلِ اسکرولِ پشتِ کشو: وگرنه کشیدنِ انگشت روی کشو، صفحه‌ی زیرش رو
    // می‌لغزوند و بعدِ بستن، کاربر یه جای دیگه‌ی صفحه بود.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  /**
   * یک ردیفِ کشو.
   *
   * `needsAuth` یعنی این مقصد پشتِ ورود است، پس مهمان به‌جاش به صفحه‌ی ورود
   * می‌ره — ولی با آدرسِ واقعی توی state، تا بعدِ ورود همون‌جا برسه که زده
   * بود (نگاه کن به AuthRoute در routes.tsx). کشف پشتِ ورود نیست و مهمان
   * باید مستقیم ببیندش، وگرنه صفحه‌ی اولِ سایت هم ازش گرفته می‌شد.
   */
  const link = (
    to: string, label: string,
    { dot = false, needsAuth = true, title }: {
      dot?: boolean; needsAuth?: boolean;
      /** راهنمای ماوس‌رو؛ فعلاً فقط «برنامه‌ی جدید» داردش. */
      title?: string;
    } = {},
  ) => {
    const bounce = guest && needsAuth;
    return (
      <Link
        to={bounce ? '/auth' : to}
        state={bounce ? { from: to } : undefined}
        className={`drawer-item${pathname === to ? ' on' : ''}`}
        title={title}
      >
        <span>{label}</span>
        {dot ? <span className="navdot" aria-hidden="true" /> : null}
      </Link>
    );
  };

  const anyDot = dots.signal || dots.event || dots.friend_request;

  return (
    <>
      <button
        type="button"
        className={`hamburger${pointing && !open ? ' tour-point' : ''}`}
        data-testid="menu-button"
        aria-label={t('menu')}
        aria-expanded={open}
        aria-controls="mobile-drawer"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <CloseIcon /> : <MenuIcon />}
        {/* وقتی کشو بازه نقطه معنی نداره — خودِ فهرست نقطه‌هاش رو نشون می‌ده. */}
        {!open && anyDot ? <span className="navdot" aria-hidden="true" /> : null}
      </button>

      {/* ═══ چرا کشو با پورتال به body می‌ره و نه همین‌جا ═══
          این دکمه داخلِ هدره، و هدر `backdrop-filter` داره. هر عنصری که
          filter یا backdrop-filter داشته باشه، برای فرزندهای
          `position: fixed`ـش خودش می‌شه «قاب» — یعنی `top: 66px; bottom: 0`
          به‌جای پنجره، نسبت به همون هدرِ ۶۶ پیکسلی حساب می‌شد. نتیجه‌ی
          واقعی: کشویی به ارتفاعِ ۴۰ پیکسل و پرده‌ای با ارتفاعِ صفر که هیچ
          کلیکی نمی‌گرفت. پورتال کشو رو بچه‌ی `body` می‌کنه، پس دوباره پنجره
          قابش می‌شه. */}
      {open ? createPortal(
        <>
          <div className="drawer-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />
          <nav id="mobile-drawer" className="drawer" aria-label={t('menu')}>
            {guest ? null : (
              <Link to="/settings" className="drawer-me">
                <Avatar url={profile?.avatarUrl} name={profile?.displayName} size={38} />
                <span className="drawer-me-name">{profile?.displayName ?? t('settings')}</span>
              </Link>
            )}

            {link('/', t('discover'), { needsAuth: false })}
            {/* همون سه تبِ هدرِ دسکتاپ (WHOSUP-157). ردیفِ «سیگنال‌ها» به
                خواستِ کاربر حذف شد — «برنامه‌های من» پوششش می‌ده و نقطه‌ی
                اعلانش هم همین‌جاست؛ فید زیرِ /signals فقط با آدرسِ مستقیم. */}
            {link('/plans', t('myPlans'), { dot: dots.signal || dots.event })}
            {link('/friends', t('friends'), { dot: dots.friend_request })}

            <hr className="hr" />

            {/* همون دو تا دکمه‌ای که روی دسکتاپ توی هدرن. روی گوشی هدر جا
                نداشت و متنشون سه‌خطی می‌شکست، پس اومدن این‌جا. */}
            {link('/suggest', t('suggestTitle'))}
            {/* تنها ردیفی که کلیکش دست‌کاری شده: برای کاربرِ واردشده ممکنه
                اول هشدارِ «هنوز دوستی نداری» بیاد، و اون پنجره نباید پشتِ
                کشوی باز ظاهر شه — پس کشو همون‌جا بسته می‌شه. لینک می‌مونه
                (نه دکمه) تا آدرسِ واقعی و «باز کردن در تبِ جدید» سرِ جاش
                باشه، و مهمان هم همون مسیرِ همیشگی رو داشته باشه: صفحه‌ی
                ورود، با مقصد توی state. */}
            {guest ? link('/signal/new', t('newSignal'), { title: t('whoIsIn') }) : (
              <Link to="/signal/new" className="drawer-item" title={t('whoIsIn')}
                onClick={(e) => {
                  if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  setOpen(false);
                  newSignal(() => nav('/signal/new'));
                }}>
                <span>{t('newSignal')}</span>
              </Link>
            )}

            <hr className="hr" />

            <button type="button" className="drawer-item" onClick={toggle}>
              <span>{isDark ? '☀' : '☾'} {t('themeTitle')}</span>
            </button>

            {guest ? (
              <Link to="/auth" state={returnHere(location)} className="btn fill block">{t('authSignIn')}</Link>
            ) : (
              <button type="button" className="drawer-item" onClick={() => void signOut()}>
                <span>{t('signOut')}</span>
              </button>
            )}
          </nav>
        </>,
        document.body,
      ) : null}
    </>
  );
}
